import { X } from "lucide-react";
import { format } from "date-fns";
import { useEffect, useRef } from "react";
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import styles from './EventPopup.module.scss';

export function EventPopup({ event, onClose, position, type }) {
    const popupRef = useRef(null);

    useEffect(() => {
        if (popupRef.current) {
            const popup = popupRef.current;
            const rect = popup.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            let left = Math.max(20, Math.min(
                position.x,
                viewportWidth - rect.width - 20
            ));

            let top = Math.max(20, Math.min(
                (viewportHeight - rect.height) / 2,
                viewportHeight - rect.height - 20
            ));

            popup.style.left = `${left}px`;
            popup.style.top = `${top}px`;
        }
    }, [position]);

    return (
        <>
            {type !== "publication_scheduled" && (
                <div
                    className="fixed inset-0 z-50 bg-black/20 flex items-center justify-center"
                    onClick={onClose}
                >
                    <div
                        ref={popupRef}
                        className="absolute bg-white rounded-lg shadow-xl w-[480px] max-h-[60vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="sticky top-0 rounded-t-lg bg-white p-6 border-b">
                            <div className="flex justify-between items-start" style={{width: '100%'}}>
                                <div className="space-y-2" style={{width: '100%'}}>
                                    <div className="flex justify-between">
                                        <div className="flex items-center gap-2">
                                            <FiberManualRecordIcon className="text-green-500" style={{ fontSize: 12 }} />
                                            <h2 className="text-base text-gray-900 font-normal">
                                                {event.title}
                                            </h2>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="bg-green-50 text-sm text-green-600 px-3 py-0.5 rounded min-w-[60px] text-center">
                                                {event?.engagementGroup}
                                            </span>
                                            <button
                                                onClick={onClose}
                                                className="text-gray-400 hover:text-gray-600"
                                            >
                                                <X className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex justify-between">
                                        <div className="text-gray-600 text-sm">
                                            {format(event.start, "hh:mm a")} <span>→</span> {format(event.end, "hh:mm a")}
                                        </div>
                                        {event?.meetingType?.toLowerCase() === "one on one meeting" ? (
                                            <div className="flex">
                                                {" "}
                                                <img
                                                src="/icons/one_on_one.svg"
                                                alt="Processing"
                                                className="w-6 h-6 mr-2"
                                                />{" "}
                                                <span style={{color: "#F59D1E"}}>One on One Meeting</span>
                                            </div>
                                            ) : (
                                            <div  className="flex">
                                                {" "}
                                                <img
                                                src="/icons/groups.svg"
                                                alt="Processing"
                                                className="w-6 h-6 mr-2"
                                                />{" "}
                                                <span style={{color: "#F59D1E"}}>Group Meeting</span>
                                                
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-primary text-sm">
                                        {type === "publication" ? "Published by" : "Organized by"}{" "}
                                        {event.username?.join(", ")}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={`overflow-y-auto ${styles.customScroll}`}>
                            <div className="p-6 pt-0">
                                {type === "publication" && (
                                    <div className="mt-3">
                                        <div className="mt-2">
                                            {event.lag && (
                                                <div className=" border border-gray-200 p-2 rounded">
                                                    <span className="text-gray-600">
                                                        Lag :
                                                    </span>{" "}
                                                    <span className="text-black font-bold">
                                                        {event.lag}
                                                    </span>
                                                </div>
                                            )}
                                            {event.report_type && (
                                                <div className="border border-gray-200 p-2 rounded mt-2">
                                                    <span className="text-gray-600">
                                                        Report Type :
                                                    </span>{" "}
                                                    <span className="text-black font-bold">
                                                        {event.report_type}
                                                    </span>
                                                </div>
                                            )}
                                            {event.planned && (
                                                <>
                                                    <div className="border border-gray-200 p-2 rounded mt-2">
                                                        <span className="text-gray-600">
                                                            Planned for :
                                                        </span>{" "}
                                                        <span className="text-primary font-bold">
                                                            {format(
                                                                event.planned,
                                                                "dd MMMM yyyy"
                                                            )}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {type !== "publication" && (
                                    <div className="mt-6">
                                        <div className="flex justify-between items-center mb-2">
                                            <h3 className="font-medium">
                                                Participants (
                                                {event.fundClients?.length || 0})
                                            </h3>
                                        </div>
                                        <div className="space-y-2">
                                            {event.fundClients?.map(
                                                (participant, index) => (
                                                    <div
                                                        key={index}
                                                        className="px-4 py-2 bg-gray-50 rounded-lg"
                                                    >
                                                        {participant}
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="mt-3">
                                    <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                                        <div>
                                            <h3 className="text-gray-900 font-medium mb-2">
                                                Participating Sectors
                                            </h3>
                                            <div className="flex flex-wrap gap-x-2">
                                                {event.sector?.length > 0 ? (
                                                    event.sector.map(
                                                        (sector, index) => (
                                                            <span
                                                                key={index}
                                                                className="text-gray-600"
                                                            >
                                                                {sector}
                                                                {index <
                                                                event.sector.length - 1
                                                                    ? ","
                                                                    : ""}
                                                            </span>
                                                        )
                                                    )
                                                ) : (
                                                    <span className="text-gray-600">NA</span>
                                                )}
                                            </div>
                                        </div>

                                        <div>
                                            {type !== "publication" && event.optOutSectors?.length > 0 && (
                                                <h3 className="text-gray-900 font-medium mb-2">
                                                    Opt Out Sectors
                                                </h3>
                                            )}
                                            <div className="flex flex-wrap gap-x-2">
                                                {event.optOutSectors?.map(
                                                    (sector, index) => (
                                                        <span
                                                            key={index}
                                                            className="text-gray-600"
                                                        >
                                                            {sector}
                                                            {index <
                                                            event.optOutSectors
                                                                .length -
                                                                1
                                                                ? ","
                                                                : ""}
                                                        </span>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
